import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';
import { Router } from '@angular/router';
import { AuthService, User } from '../services/auth.service';
import { SearchService, SearchListing } from '../services/search.service';
import { WishlistService } from '../services/wishlist.service';
import { TutorialService } from '../services/tutorial.service';
import { TutorialComponent } from '../tutorial/tutorial.component';
import {
  catchError,
  of,
  Subject,
  takeUntil,
  forkJoin,
  combineLatest,
  delay,
} from 'rxjs';
import { FloatingChatButtonComponent } from '../floating-chat-button/floating-chat-button.component';

interface Listing {
  id: number;
  title: string;
  price: number;
  category: string;
  location: string;
  date: Date;
  image: string;
  images: string[];
  currentImageIndex: number;
  new?: boolean;
  featured?: boolean;
  currency?: string;
  subcategory?: string;
  subcategoryDisplay?: string;
  table_name?: string;
  view_count?: number;
  seller?: {
    first_name: string;
    last_name: string;
  };
  preloadedImages?: Set<number>;
  isImageLoading?: boolean;
  touchStartX?: number;
  touchStartY?: number;
  touchStartTime?: number;
  showCarouselControls?: boolean;
  controlsTimeout?: any;
  hasShownControls?: boolean;
  isTouchScrolling?: boolean;
  touchMoved?: boolean;
  lastTouchX?: number;
  lastTouchY?: number;
}

interface Testimonial {
  id: number;
  name: string;
  role: string;
  text: string;
  avatar: string;
  rating: number;
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    HeaderComponent,
    FooterComponent,
    FloatingChatButtonComponent,
    TutorialComponent,
  ],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
})
export class HomeComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('tutorial') tutorialComponent!: TutorialComponent;
  private destroy$ = new Subject<void>();

  userName = '';
  isLoggedIn = false;
  user: User | null = null;
  shouldShowTutorialComponent = false;
  private pendingTutorialShow = false;

  featuredListings: Listing[] = [];
  recentListings: Listing[] = [];
  isLoadingFeatured = true;
  isLoadingRecent = true;

  loadedImages: Set<string> = new Set();

  private readonly SWIPE_THRESHOLD = 30;
  private readonly SWIPE_FAST_THRESHOLD = 20;
  private readonly SWIPE_VELOCITY_THRESHOLD = 0.3;
  private readonly SWIPE_TIME_THRESHOLD = 600;
  private readonly VERTICAL_SCROLL_THRESHOLD = 25;
  private readonly TAP_MOVEMENT_THRESHOLD = 10;
  private readonly TAP_TIME_THRESHOLD = 300;

  testimonials: Testimonial[] = [
    {
      id: 1,
      name: 'Zoran Jovanovski',
      role: 'Buyer',
      text: 'Markado made finding my dream car so easy! I was able to compare prices, negotiate with the seller, and complete the purchase all within a week. Highly recommend!',
      avatar: '/assets/images/avatar-placeholder/avatar-placeholder.png',
      rating: 5,
    },
    {
      id: 2,
      name: 'Adem Aliu',
      role: 'Seller',
      text: "I've sold items on many platforms before, but Markado gives me the most visibility and serious buyers. Sold my electronics and furniture in just days!",
      avatar: '/assets/images/avatar-placeholder/avatar-placeholder.png',
      rating: 5,
    },
    {
      id: 3,
      name: 'Damjan Savevski',
      role: 'Regular User',
      text: 'The interface is intuitive, listings are organized well, and the customer service is outstanding. Markado has become my go-to marketplace for everything.',
      avatar: '/assets/images/avatar-placeholder/avatar-placeholder.png',
      rating: 5,
    },
  ];

  constructor(
    private router: Router,
    private authService: AuthService,
    private searchService: SearchService,
    private wishlistService: WishlistService,
    private tutorialService: TutorialService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.userName = 'John';

    combineLatest([this.authService.isLoggedIn$, this.authService.currentUser$])
      .pipe(takeUntil(this.destroy$), delay(100))
      .subscribe(([isLoggedIn, user]) => {
        this.isLoggedIn = isLoggedIn;
        this.user = user;
        this.checkAndShowTutorial();
      });

    this.loadListings();

    setTimeout(() => {
      document.querySelector('.home-content')?.classList.add('loaded');
    }, 100);

    this.testimonials.forEach((testimonial) => {
      if (!testimonial.avatar.startsWith('http')) {
        testimonial.avatar =
          '/assets/images/avatar-placeholder/avatar-placeholder.png';
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.pendingTutorialShow && this.tutorialComponent) {
      setTimeout(() => {
        this.tutorialComponent.showTutorialPrompt();
        this.pendingTutorialShow = false;
      }, 1500);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkAndShowTutorial(): void {
    this.pendingTutorialShow = false;

    if (!this.isLoggedIn || !this.user || this.user.is_admin) {
      this.shouldShowTutorialComponent = false;
      return;
    }

    if (this.tutorialService.shouldShowTutorial()) {
      this.shouldShowTutorialComponent = true;
      this.cdr.detectChanges();
      setTimeout(() => {
        if (this.tutorialComponent) {
          this.tutorialComponent.showTutorialPrompt();
        } else {
          this.pendingTutorialShow = true;
        }
      }, 1500);
    } else {
      this.shouldShowTutorialComponent = false;
    }
  }

  private loadListings(): void {
    this.isLoadingFeatured = true;
    this.isLoadingRecent = true;

    forkJoin({
      featured: this.searchService.getFeaturedListings({}, 1, 5, 'newest'),
      recent: this.searchService.getRecentListings({}, 1, 3, 'newest'),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          this.featuredListings = results.featured.listings
            .slice(0, 5)
            .map((listing) => {
              const converted = this.convertToListing(listing);
              converted.featured = true;
              const daysSinceCreation = Math.floor(
                (new Date().getTime() -
                  new Date(listing.created_at).getTime()) /
                  (1000 * 60 * 60 * 24)
              );
              converted.new = daysSinceCreation <= 3;
              return converted;
            });

          this.recentListings = results.recent.listings
            .slice(0, 3)
            .map((listing) => {
              const converted = this.convertToListing(listing);
              converted.new = true;
              converted.featured = listing.is_featured;
              return converted;
            });

          this.isLoadingFeatured = false;
          this.isLoadingRecent = false;

          this.loadWishlistStatusForAllListings();
        },
        error: (error) => {
          console.error('Error loading listings:', error);
          this.isLoadingFeatured = false;
          this.isLoadingRecent = false;
          this.featuredListings = [];
          this.recentListings = [];
        },
      });
  }

  private loadWishlistStatusForAllListings(): void {
    if (!this.authService.isLoggedIn()) {
      return;
    }

    const allListings = [...this.featuredListings, ...this.recentListings];

    if (allListings.length === 0) return;

    const listingsForWishlist = allListings
      .map((listing) => ({
        id: listing.id.toString(),
        table_name: listing.table_name || '',
      }))
      .filter(
        (item, index, self) =>
          item.table_name && self.findIndex((l) => l.id === item.id) === index
      );

    if (listingsForWishlist.length === 0) return;

    this.wishlistService
      .checkWishlistStatus(listingsForWishlist)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.warn('Failed to load wishlist status:', error);
          return of({});
        })
      )
      .subscribe({
        next: (wishlistStatus) => {},
      });
  }

  onListingTouchStart(event: TouchEvent, listing: Listing): void {
    const touch = event.touches[0];
    listing.touchStartX = touch.clientX;
    listing.touchStartY = touch.clientY;
    listing.lastTouchX = touch.clientX;
    listing.lastTouchY = touch.clientY;
    listing.touchStartTime = Date.now();
    listing.isTouchScrolling = false;
    listing.touchMoved = false;
  }

  onListingTouchMove(event: TouchEvent, listing: Listing): void {
    if (
      listing.touchStartX === undefined ||
      listing.touchStartY === undefined ||
      listing.lastTouchX === undefined ||
      listing.lastTouchY === undefined
    ) {
      return;
    }

    const touch = event.touches[0];
    const deltaX = Math.abs(touch.clientX - listing.touchStartX);
    const deltaY = Math.abs(touch.clientY - listing.touchStartY);

    if (
      deltaY > this.TAP_MOVEMENT_THRESHOLD ||
      deltaX > this.TAP_MOVEMENT_THRESHOLD
    ) {
      listing.touchMoved = true;

      if (deltaY > deltaX && deltaY > this.VERTICAL_SCROLL_THRESHOLD) {
        listing.isTouchScrolling = true;
      } else if (deltaX > deltaY && deltaX > 5 && listing.images.length > 1) {
        event.preventDefault();
      }
    }

    listing.lastTouchX = touch.clientX;
    listing.lastTouchY = touch.clientY;
  }

  onListingTouchEnd(event: TouchEvent, listing: Listing): void {
    setTimeout(() => {
      listing.isTouchScrolling = false;
      listing.touchMoved = false;
      listing.touchStartX = undefined;
      listing.touchStartY = undefined;
      listing.touchStartTime = undefined;
      listing.lastTouchX = undefined;
      listing.lastTouchY = undefined;
    }, 100);
  }

  private convertToListing(searchListing: SearchListing): Listing {
    const images = searchListing.images || [];
    const firstImage =
      images.length > 0
        ? images[0]
        : this.getDefaultImage(searchListing.category);

    return {
      id: searchListing.id,
      title: searchListing.title,
      price: searchListing.price,
      category: this.formatCategoryName(searchListing.category),
      location: searchListing.location || 'Location not specified',
      date: new Date(searchListing.created_at),
      image: firstImage,
      images: images.length > 0 ? images : [firstImage],
      currentImageIndex: 0,
      currency: searchListing.currency,
      subcategory: searchListing.subcategory,
      subcategoryDisplay: this.formatSubcategoryName(searchListing.subcategory),
      featured: searchListing.is_featured,
      table_name: this.getTableNameFromListing(searchListing),
      view_count: searchListing.view_count || 0,
      seller: searchListing.seller
        ? {
            first_name: searchListing.seller.first_name || 'Unknown',
            last_name: searchListing.seller.last_name || '',
          }
        : undefined,
      preloadedImages: new Set([0]),
      isImageLoading: false,
      showCarouselControls: false,
      hasShownControls: false,
      isTouchScrolling: false,
      touchMoved: false,
    };
  }

  private isWishlistButtonTouch(event: TouchEvent): boolean {
    const target = event.target as HTMLElement;
    return !!(
      target.closest('.listing-bookmark') ||
      target.closest('app-wishlist-heart')
    );
  }

  onTouchStart(event: TouchEvent, listing: Listing): void {
    if (this.isWishlistButtonTouch(event)) {
      return;
    }

    const touch = event.touches[0];
    listing.touchStartX = touch.clientX;
    listing.touchStartY = touch.clientY;
    listing.lastTouchX = touch.clientX;
    listing.lastTouchY = touch.clientY;
    listing.touchStartTime = Date.now();
    listing.isTouchScrolling = false;
    listing.touchMoved = false;
  }

  onTouchMove(event: TouchEvent, listing: Listing): void {
    if (this.isWishlistButtonTouch(event)) {
      return;
    }

    if (
      listing.touchStartX === undefined ||
      listing.touchStartY === undefined
    ) {
      return;
    }

    const touch = event.touches[0];
    const deltaX = Math.abs(touch.clientX - listing.touchStartX);
    const deltaY = Math.abs(touch.clientY - listing.touchStartY);

    if (
      deltaY > this.TAP_MOVEMENT_THRESHOLD ||
      deltaX > this.TAP_MOVEMENT_THRESHOLD
    ) {
      listing.touchMoved = true;
    }

    if (deltaY > deltaX && deltaY > this.VERTICAL_SCROLL_THRESHOLD) {
      listing.isTouchScrolling = true;
    } else if (
      deltaX > deltaY &&
      deltaX > this.TAP_MOVEMENT_THRESHOLD &&
      listing.images.length > 1
    ) {
      event.preventDefault();
    }

    listing.lastTouchX = touch.clientX;
    listing.lastTouchY = touch.clientY;
  }

  onTouchEnd(event: TouchEvent, listing: Listing): void {
    if (this.isWishlistButtonTouch(event)) {
      this.resetTouchState(listing);
      return;
    }

    if (listing.images.length <= 1) {
      if (!listing.isTouchScrolling && !listing.touchMoved) {
        this.onListingClick(listing);
      }
      this.resetTouchState(listing);
      return;
    }

    if (
      listing.touchStartX === undefined ||
      listing.touchStartY === undefined ||
      listing.touchStartTime === undefined
    ) {
      return;
    }

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - listing.touchStartX;
    const deltaY = touch.clientY - listing.touchStartY;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);
    const deltaTime = Date.now() - listing.touchStartTime;

    if (
      listing.isTouchScrolling ||
      (absDeltaY > this.VERTICAL_SCROLL_THRESHOLD && absDeltaY > absDeltaX)
    ) {
      this.resetTouchState(listing);
      return;
    }

    const velocity = deltaTime > 0 ? absDeltaX / deltaTime : 0;
    const isHorizontalSwipe = absDeltaX > absDeltaY;
    const isQuickEnough = deltaTime < this.SWIPE_TIME_THRESHOLD;
    const isFastSwipe =
      velocity >= this.SWIPE_VELOCITY_THRESHOLD &&
      absDeltaX >= this.SWIPE_FAST_THRESHOLD;
    const isLongEnough = absDeltaX > this.SWIPE_THRESHOLD || isFastSwipe;
    const isNotTooVertical = absDeltaY < this.VERTICAL_SCROLL_THRESHOLD;

    if (
      isHorizontalSwipe &&
      isQuickEnough &&
      isLongEnough &&
      isNotTooVertical
    ) {
      event.stopPropagation();
      event.preventDefault();
      if (deltaX > 0) {
        this.previousImage(listing, event);
      } else {
        this.nextImage(listing, event);
      }
    } else if (
      !listing.touchMoved ||
      (absDeltaX <= this.TAP_MOVEMENT_THRESHOLD &&
        absDeltaY <= this.TAP_MOVEMENT_THRESHOLD &&
        deltaTime <= this.TAP_TIME_THRESHOLD)
    ) {
      this.onListingClick(listing);
    }
    this.resetTouchState(listing);
  }

  private resetTouchState(listing: Listing): void {
    setTimeout(() => {
      listing.touchStartX = undefined;
      listing.touchStartY = undefined;
      listing.touchStartTime = undefined;
      listing.lastTouchX = undefined;
      listing.lastTouchY = undefined;
      listing.isTouchScrolling = false;
      listing.touchMoved = false;
    }, 100);
  }

  onImageZoneClick(
    event: Event,
    listing: Listing,
    zone: 'prev' | 'next' | 'view'
  ): void {
    event.stopPropagation();

    if (zone === 'view') {
      this.onListingClick(listing);
    } else if (zone === 'prev') {
      this.previousImage(listing, event);
    } else if (zone === 'next') {
      this.nextImage(listing, event);
    }
  }

  previousImage(listing: Listing, event: Event): void {
    event.stopPropagation();

    if (listing.images.length <= 1) return;

    const newIndex =
      (listing.currentImageIndex - 1 + listing.images.length) %
      listing.images.length;

    this.navigateToImage(listing, newIndex);
  }

  nextImage(listing: Listing, event: Event): void {
    event.stopPropagation();

    if (listing.images.length <= 1) return;

    const newIndex = (listing.currentImageIndex + 1) % listing.images.length;

    this.navigateToImage(listing, newIndex);
  }

  private navigateToImage(listing: Listing, targetIndex: number): void {
    listing.isImageLoading = true;
    listing.currentImageIndex = targetIndex;
    listing.image = listing.images[targetIndex];

    if (!listing.preloadedImages) {
      listing.preloadedImages = new Set();
    }
    listing.preloadedImages.add(targetIndex);

    this.preloadAdjacentImages(listing, targetIndex);
  }

  private preloadAdjacentImages(listing: Listing, currentIndex: number): void {
    if (listing.images.length <= 1) return;

    const nextIndex = (currentIndex + 1) % listing.images.length;
    const prevIndex =
      (currentIndex - 1 + listing.images.length) % listing.images.length;

    if (!listing.preloadedImages?.has(nextIndex)) {
      this.preloadImage(listing.images[nextIndex]);
      listing.preloadedImages?.add(nextIndex);
    }

    if (!listing.preloadedImages?.has(prevIndex)) {
      this.preloadImage(listing.images[prevIndex]);
      listing.preloadedImages?.add(prevIndex);
    }
  }

  private preloadImage(url: string): void {
    if (this.loadedImages.has(url)) return;

    const img = new Image();
    img.src = url;
    img.onload = () => {
      this.loadedImages.add(url);
    };
    img.onerror = () => {
      console.warn(`Failed to preload image: ${url}`);
    };
  }

  onListingHover(listing: Listing): void {
    if (listing.images.length <= 1) return;

    listing.images.forEach((imageUrl, index) => {
      if (!listing.preloadedImages?.has(index)) {
        this.preloadImage(imageUrl);
        listing.preloadedImages?.add(index);
      }
    });

    if (!listing.hasShownControls) {
      listing.showCarouselControls = true;
      listing.hasShownControls = true;

      if (listing.controlsTimeout) {
        clearTimeout(listing.controlsTimeout);
      }

      listing.controlsTimeout = setTimeout(() => {
        listing.showCarouselControls = false;
      }, 2000);
    }
  }

  onListingLeave(listing: Listing): void {
    if (listing.controlsTimeout) {
      clearTimeout(listing.controlsTimeout);
    }
    listing.hasShownControls = false;
    listing.showCarouselControls = false;
  }

  hasMultipleImages(listing: Listing): boolean {
    return listing.images.length > 1;
  }

  getImageCounter(listing: Listing): string {
    return `${listing.currentImageIndex + 1}/${listing.images.length}`;
  }

  onImageLoad(imageUrl: string, listing?: Listing): void {
    this.loadedImages.add(imageUrl);
    if (listing) {
      listing.isImageLoading = false;
    }
  }

  isImageLoaded(imageUrl: string): boolean {
    return this.loadedImages.has(imageUrl);
  }

  private getTableNameFromListing(listing: SearchListing): string {
    const tableMap: { [key: string]: string } = {
      'vehicles-automobile': 'attributes_automobile',
      'vehicles-motorcycle': 'attributes_motorcycle',
      'vehicles-commercial-vehicles': 'attributes_commercial_vehicles',
      'vehicles-oldtimer': 'attributes_oldtimer',
      'vehicles-nautical': 'attributes_nautical',
      'vehicles-spare-parts': 'attributes_spare_parts',
      'vehicles-campers-caravans': 'attributes_campers_caravans',
      'vehicles-crashed-cars': 'attributes_crashed_cars',
      'vehicles-construction': 'attributes_construction',
      'vehicles-agriculture': 'attributes_agriculture',
      'real-estate-house-sale': 'attributes_house_sale',
      'real-estate-house-rent': 'attributes_house_rent',
      'real-estate-apartments-sale': 'attributes_apartments_sale',
      'real-estate-apartments-rent': 'attributes_apartments_rent',
      'real-estate-land-sale': 'attributes_land_sale',
      'real-estate-land-rent': 'attributes_land_rent',
      'real-estate-business-office-sale': 'attributes_business_office_sale',
      'real-estate-business-office-rent': 'attributes_business_office_rent',
      'real-estate-garage-sale': 'attributes_garage_sale',
      'real-estate-garage-rent': 'attributes_garage_rent',
      'real-estate-luxury-real-estate-sale':
        'attributes_luxury_real_estate_sale',
      'real-estate-luxury-real-estate-rent':
        'attributes_luxury_real_estate_rent',
      'real-estate-room-rent': 'attributes_room_rent',
      'real-estate-looking-for-roommate': 'attributes_roommate',
      'marketplace-home': 'attributes_home',
      'marketplace-electronics': 'attributes_electronics',
      'marketplace-sport': 'attributes_sport',
      'marketplace-children': 'attributes_children',
      'marketplace-head-to-toe': 'attributes_head_to_toe',
      'marketplace-machines-tools': 'attributes_machines_tools',
      'marketplace-media': 'attributes_media',
      'marketplace-musical-instruments': 'attributes_musical_instruments',
      'marketplace-literature': 'attributes_literature',
      'marketplace-pets': 'attributes_pets',
      'marketplace-other': 'attributes_other',
      'marketplace-giving-away': 'attributes_giving_away',
      'jobs-professional': 'attributes_professional',
      'jobs-home-maintenance': 'attributes_home_maintenance',
      'jobs-personal-care-wellness': 'attributes_personal_care_wellness',
      'jobs-events-entertainment': 'attributes_events_entertainment',
      'jobs-education-training': 'attributes_education_training',
      'jobs-transport-delivery': 'attributes_transport_delivery',
    };

    const key = `${listing.category}-${listing.subcategory}`;
    return (
      tableMap[key] || `attributes_${listing.subcategory.replace(/-/g, '_')}`
    );
  }

  private formatCategoryName(category: string): string {
    const categoryMap: { [key: string]: string } = {
      vehicles: 'Vehicles',
      'real-estate': 'Real Estate',
      marketplace: 'Marketplace',
      jobs: 'Jobs',
    };
    return categoryMap[category] || category;
  }

  private formatSubcategoryName(subcategory: string): string {
    if (!subcategory) return '';
    return subcategory
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private getDefaultImage(category: string): string {
    const defaultImages: { [key: string]: string } = {
      vehicles: '/assets/images/listing-placeholder/no-image-placeholder.png',
      'real-estate':
        '/assets/images/listing-placeholder/no-image-placeholder.png',
      marketplace:
        '/assets/images/listing-placeholder/no-image-placeholder.png',
      jobs: '/assets/images/listing-placeholder/no-image-placeholder.png',
    };
    return (
      defaultImages[category] ||
      '/assets/images/listing-placeholder/no-image-placeholder.png'
    );
  }

  formatDate(date: Date): string {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    }
  }

  onSearchKeydown(event: KeyboardEvent, query: string, category: string): void {
    if (
      event.key === 'Enter' ||
      event.code === 'Enter' ||
      event.keyCode === 13
    ) {
      event.preventDefault();
      this.searchListings(query, category);
    }
  }

  searchListings(query: string, category: string = 'all'): void {
    const searchKeyword = query.trim();
    const searchCategory = category.toLowerCase();

    if (!searchKeyword && searchCategory === 'all') {
      alert('Please select a category or enter what you are looking for.');
      return;
    }

    if (searchKeyword && searchCategory === 'all') {
      this.router.navigate([
        '/search',
        'all',
        encodeURIComponent(searchKeyword),
      ]);
      return;
    }

    if (!searchKeyword && searchCategory !== 'all') {
      this.router.navigate(['/search', searchCategory]);
      return;
    }

    this.router.navigate([
      '/search',
      searchCategory,
      encodeURIComponent(searchKeyword),
    ]);
  }

  onListingClick(listing: Listing): void {
    if (listing.isTouchScrolling || listing.touchMoved) {
      return;
    }

    if (listing.touchStartTime !== undefined) {
      const tapDuration = Date.now() - listing.touchStartTime;
      if (tapDuration > this.TAP_TIME_THRESHOLD) {
        return;
      }
    }

    if (listing.subcategory) {
      this.router.navigate([
        '/listing-details',
        listing.subcategory,
        listing.id,
      ]);
    } else {
      this.router.navigate(['/listing', listing.id]);
    }
  }

  canUseWishlist(): boolean {
    return this.wishlistService.canUseWishlist();
  }
}
